import React, { useState, useEffect } from "react";
import "../components/styles.css";
import {
  Clock,
  Phone,
  Users,
  Heart,
  ScrollText,
  HandCoins,
  MessageCircle,
} from "lucide-react";

const HomePage = ({ setCurrentPage }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // 4 slides using images from /public/images
  const slides = [
    {
      title: "Welcome to Sri Kalikamundeshwari Temple",
      subtitle: "A sacred place of peace and spirituality",
      image: "/images/img59.jpeg",
      icon: "🏛️",
      ctaPage: "gallery",
    },
    {
      title: "Daily Prayers & Rituals",
      subtitle: "Join us for morning and evening prayers",
      image: "/images/img5.jpg",
      icon: "🙏",
      ctaPage: "gallery",
    },
    {
      title: "Community Gatherings",
      subtitle: "Bringing devotees together in faith and devotion",
      image: "/images/img63.jpeg",
      icon: "👥",
      ctaPage: "about",
    },
    {
      title: "Support Temple Development",
      subtitle: "Contribute towards annadanam, festivals and renovation",
      image: "/images/img35.jpeg",
      icon: "❤️",
      ctaPage: "donation",
    },
  ];

  const activeSlide = slides[currentSlide];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Updated navigation helper to use setCurrentPage
  const navigateToPage = (pageId) => {
    setCurrentPage(pageId);
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="animate-fadeIn">
      {/* Hero Section with Full Cover Image Slider - FULL WIDTH TEXT */}
      <div className="relative h-[500px] sm:h-[550px] md:h-[600px] lg:h-[650px] overflow-hidden">
        {/* Full cover image - fills entire container */}
        <div className="absolute inset-0">
          <img
            src={activeSlide.image}
            alt="Temple slide"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 30%" }}
          />
          {/* Gradient overlay - NO overlay at top/center (deity fully visible), ONLY at bottom for text */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent from-0% via-transparent via-60% to-black/80 to-100%" />
        </div>

        {/* Text content - Positioned at VERY BOTTOM */}
        <div className="relative z-10 flex items-end justify-center h-full text-center text-white px-4 sm:px-6 md:px-8 lg:px-12 pb-10 sm:pb-14 md:pb-16">
          <div className="animate-slideUp w-full max-w-5xl">
            {/* Icon - Centered and smaller */}
            <div className="flex items-center justify-center mb-2 sm:mb-3">
              <span className="text-3xl sm:text-4xl md:text-5xl">
                {activeSlide.icon}
              </span>
            </div>

            {/* Title - REDUCED SIZE with gradient color */}
            <h1
              className="
                text-xl sm:text-2xl md:text-3xl lg:text-4xl 
                font-extrabold uppercase tracking-wide
                bg-gradient-to-r from-yellow-300 to-orange-200
                text-transparent bg-clip-text
                leading-tight mb-2 sm:mb-3
                px-2 sm:px-4
              "
              style={{
                WebkitTextStroke: "1px rgba(0,0,0,0.3)",
                filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.8))",
              }}
            >
              {activeSlide.title}
            </h1>

            {/* Subtitle - Smaller with better readability */}
            <p
              className="
              text-xs sm:text-sm md:text-base 
              mb-4 sm:mb-5 
              text-gray-100 
              max-w-3xl mx-auto px-4
              leading-relaxed font-medium
            "
              style={{
                textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
              }}
            >
              {activeSlide.subtitle}
            </p>

            {/* CTA Buttons - Side by side, smaller */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-center">
              <button
                onClick={() => navigateToPage(activeSlide.ctaPage)}
                className="
                  bg-yellow-400 text-orange-900 
                  px-5 sm:px-6 py-2 sm:py-2.5
                  rounded-full font-bold uppercase tracking-wide
                  hover:bg-yellow-300 
                  transform hover:scale-105 
                  transition-all duration-300 
                  shadow-2xl
                  text-xs sm:text-sm
                  active:scale-95
                  min-w-[160px]
                "
              >
                Explore Temple
              </button>

              <button
                onClick={() => navigateToPage("donation")}
                className="
                  bg-white text-orange-900 
                  px-5 sm:px-6 py-2 sm:py-2.5
                  rounded-full font-bold uppercase tracking-wide
                  hover:bg-gray-100 
                  transform hover:scale-105 
                  transition-all duration-300 
                  shadow-2xl
                  text-xs sm:text-sm
                  active:scale-95
                  min-w-[160px]
                "
              >
                Donate Now
              </button>
            </div>
          </div>
        </div>

        {/* Slide indicators - Positioned above text area */}
        <div className="absolute bottom-4 sm:bottom-5 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`
                w-2.5 h-2.5 sm:w-3 sm:h-3 
                rounded-full border border-white/60 
                transition-all duration-300 
                ${
                  index === currentSlide
                    ? "bg-yellow-400 scale-110 sm:scale-125"
                    : "bg-white/40 hover:bg-white"
                }
              `}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Temple Highlights / Main Actions - Mobile optimized */}
      <div className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4 text-gray-800 animate-slideUp">
            Temple Highlights
          </h2>
          <p className="text-center text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Book temple services, offer donations and access online facilities
            from the comfort of your home.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Contact */}
            <button
              type="button"
              onClick={() => navigateToPage("contact")}
              className="
                text-left p-5 sm:p-6 
                bg-white rounded-lg 
                shadow-lg hover:shadow-xl 
                transform hover:-translate-y-2 
                transition-all duration-300 
                animate-slideUp 
                border border-orange-100 
                hover:border-orange-300
                active:scale-95
              "
              style={{ animationDelay: "0ms" }}
            >
              <div className="mb-3 sm:mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 via-blue-50 to-indigo-100 text-blue-700 shadow-inner ring-1 ring-blue-200">
                <MessageCircle
                  className="h-8 w-8 sm:h-9 sm:w-9"
                  strokeWidth={1.8}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Contact</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Reach the temple office, request guidance and connect with the
                community.
              </p>
            </button>

            {/* Donations */}
            <button
              type="button"
              onClick={() => navigateToPage("donation")}
              className="
                text-left p-5 sm:p-6 
                bg-white rounded-lg 
                shadow-lg hover:shadow-xl 
                transform hover:-translate-y-2 
                transition-all duration-300 
                animate-slideUp 
                border border-orange-100 
                hover:border-orange-300
                active:scale-95
              "
              style={{ animationDelay: "200ms" }}
            >
              <div className="mb-3 sm:mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-100 via-orange-50 to-amber-100 text-red-500 shadow-inner ring-1 ring-red-100">
                <HandCoins
                  className="h-8 w-8 sm:h-9 sm:w-9"
                  strokeWidth={1.8}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Donations
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Support temple maintenance, annadanam, festivals and development
                works.
              </p>
            </button>

            {/* Online Services */}
            <button
              type="button"
              onClick={() => navigateToPage("online-services")}
              className="
                text-left p-5 sm:p-6 
                bg-white rounded-lg 
                shadow-lg hover:shadow-xl 
                transform hover:-translate-y-2 
                transition-all duration-300 
                animate-slideUp 
                border border-orange-100 
                hover:border-orange-300
                active:scale-95
                sm:col-span-2 md:col-span-1
              "
              style={{ animationDelay: "400ms" }}
            >
              <div className="mb-3 sm:mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 via-orange-50 to-amber-100 text-slate-700 shadow-inner ring-1 ring-slate-200">
                <ScrollText
                  className="h-8 w-8 sm:h-9 sm:w-9"
                  strokeWidth={1.8}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Online Services
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Book services, get prasadam delivery and connect with the temple
                online.
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* About Us Section - Mobile optimized with FIXED IMAGE */}
      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-center">
          {/* Text */}
          <div className="animate-slideUp">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-gray-800">
              About Sri Kalikamundeshwari Temple
            </h2>
            <p className="text-gray-700 mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base">
              Sri Kalikamundeshwari Temple is a sacred centre of devotion and
              spiritual energy. For decades, devotees have visited this temple
              to seek blessings, inner peace and divine guidance.
            </p>
            <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              The temple conducts regular poojas, homas, special sevas and
              annual festivals that bring the community together. Annadanam and
              other charitable activities are organised to serve devotees and
              the needy.
            </p>
            <button
              type="button"
              onClick={() => navigateToPage("about")}
              className="
                inline-flex items-center 
                bg-orange-600 text-white 
                px-5 sm:px-6 py-2.5 sm:py-3 
                rounded-full font-semibold 
                hover:bg-orange-700 
                transform hover:scale-105 
                transition-all duration-300 
                shadow-md
                text-sm sm:text-base
                active:scale-95
              "
            >
              Read More
              <span className="ml-2 text-base sm:text-lg">→</span>
            </button>
          </div>

          {/* Image - UPDATED to show full deity image */}
          <div
            className="animate-slideUp md:pl-6 mt-6 md:mt-0"
            style={{ animationDelay: "150ms" }}
          >
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-orange-100 bg-gray-100">
              <img
                src="/images/img59.jpeg"
                alt="Sri Kalikamundeshwari Temple"
                className="w-full h-64 sm:h-80 md:h-96 object-contain"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white">
                <p className="text-xs sm:text-sm uppercase tracking-wide text-orange-200">
                  Divine Presence
                </p>
                <p className="text-sm sm:text-base md:text-lg font-semibold">
                  A serene space for prayer and meditation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info Section - Mobile optimized */}
      <div className="py-12 sm:py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
            {[
              { icon: Clock, title: "Temple Hours", desc: "6:00 AM - 9:00 PM" },
              { icon: Phone, title: "Contact", desc: "+91 98454 11941" },
              { icon: Users, title: "Community", desc: "5000+ Devotees" },
              { icon: Heart, title: "Established", desc: "Since 1993" },
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className="animate-slideUp"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <IconComponent
                    className="mx-auto mb-3 sm:mb-4 animate-bounce"
                    size={28}
                  />
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-1 sm:mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
